// tslint:disable: no-redundant-jsdoc
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SyncError } from '@vending/sync-engine-client';
import { Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { EndpointService } from './endpoint.service';
import { GlobalHelper } from '../helpers/globalHelper/global.helper';
import { resolvePageSize } from '../helpers/page-size';
import { BasicModel } from '../models/basic';
import { ResponseModel } from '../models/response';
import { ErrorService } from './error.service';
import { IDataService } from './idataService';

/** Query-Parameter in der Form, die HttpClient annimmt */
type HttpQueryParams = Record<
  string,
  string | number | boolean | ReadonlyArray<string | number | boolean>
>;

@Injectable({
  providedIn: 'root',
})
export class DataService<T extends BasicModel>
  extends EndpointService
  implements IDataService<T>
{
  public currentlySaving = false;

  public removeParams = ['created_at', 'updated_at'];
  public attributedParams: string[] = [];

  /**
   * HTTP Options
   */
  public static httpOptions(params: Record<string, any>): Record<string, any> {
    params = params || {};
    params['Content-Type'] = 'application/json';

    // if params value is empty remove
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === undefined) {
        delete params[key];
      }
    });

    return {
      headers: new HttpHeaders(params),
    };
  }
  /**
   * HTTP Options
   */
  public httpOptions(params: Record<string, any>): Record<string, any> {
    return DataService.httpOptions(params);
  }

  /**
   * Default-Constructor
   * @param {HttpClient} http
   * @param {ErrorService} errorService
   */
  constructor(public http: HttpClient, public errorService: ErrorService) {
    super();
  }

  allRemote(): Observable<ResponseModel<T>> {
    return this.http
      .get<ResponseModel<T>>(this.endpointWithUrl)
      .pipe(retry(1), catchError(this.errorService.convert));
  }
  findRemote(id: number): Observable<T> {
    return this.http
      .get<T>(this.endpointWithUrl + id)
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  bulkFindRemote(ids: number[]): Observable<T[]> {
    return this.http
      .get<T[]>(`${this.endpointWithUrl}/bulk/${ids.join(',')}`)
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  whereRemote(args: any): Observable<ResponseModel<T>> {
    return this.http
      .get<ResponseModel<T>>(this.endpointWithUrl, { params: args })
      .pipe(retry(1), catchError(this.errorService.convert));
  }
  deleteRemote(id: number): Observable<T> {
    return this.delete(id);
  }

  /**
   * Objekt tief clonen. Ist nicht sehr performance orientiert, aber am simpelsten
   * @param {any} object
   * @return {any}
   */
  public cloneObjekt(object: any): any {
    return GlobalHelper.deepClone(object);
  }

  /**
   * Entfernt Parameter welche nicht gesendet werden sollen
   * @param hash to remove params of
   */
  removeNotToUseParams(hash: any) {
    // Neues Objekt erstellen
    if (hash === undefined || hash == null) return;
    this.removeParams.forEach(p => {
      delete hash[p];
    });
    Object.keys(hash).forEach(element => {
      if (typeof hash[element] === 'object')
        this.removeNotToUseParams(hash[element]);
    });
  }

  /**
   * Remove not used attributes
   * @param {any} hash
   */
  renameAttributedParams(hash: any) {
    if (hash === undefined || hash == null) return;
    for (const k of Object.keys(hash)) {
      if (typeof hash[k] === 'object') {
        this.renameAttributedParams(hash[k]);
      }
      if (this.attributedParams.includes(k)) {
        const v = hash[k];
        delete hash[k];
        hash[k + '_attributes'] = v;
      }
    }
    return hash;
  }

  // NOTE(Steffen): Hier könnte man doch überall an die Remote-Methoden von oben delegieren?

  /**
   * Erhält alle Objecte vom Endpunkt.
   */
  all(): Observable<ResponseModel<T>> {
    return this.http
      .get<ResponseModel<T>>(this.endpointWithUrl)
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Ruft ein Objekt mit Details ab
   * @param id
   */
  find(id: number): Observable<any> {
    return this.http
      .get<any>(this.endpointWithUrl + id)
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Sucht Objekte mit Argumenten
   * @param args
   */
  where(args: any): Observable<ResponseModel<T>> {
    return this.http
      .get<ResponseModel<T>>(this.endpointWithUrl, {
        params: this.withPageSize(args),
      })
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Ergänzt die Seitengröße für paginierte Abrufe. Das Backend begrenzt den
   * Wert seinerseits; Abrufe ohne Seitenangabe bleiben unverändert.
   * @param args Parameter des Abrufs
   */
  private withPageSize(args: HttpQueryParams): HttpQueryParams {
    if (!args?.page) return args;
    return { ...args, items: resolvePageSize() };
  }

  /**
   * Erzeugt/Speichert ein Objekt im Backend (je nachdem ob ID gesetzt)
   * @param object Zu speicherndes/erzeugendes Objekt
   * @param queueId
   */
  save(object: any, queueId: string | null = null): Observable<T> {
    if (object.id && object.id >= 0) {
      return this.update(object.id, object, queueId);
    } else {
      return this.create(object, queueId);
    }
  }

  /**
   * Erzeugt ein neues Objekt im Backend
   * @param object
   * @param queueId Id der Queue für Idempotency
   */
  private create(object: any, queueId: string | null): Observable<T> {
    delete object.id;
    return this.http
      .post<T>(
        this.endpointWithUrl,
        this.formatPayload(object),
        this.httpOptions({ 'Idempotency-Key': object.client_id ?? queueId }),
      )
      .pipe(catchError(this.errorService.convert));
  }

  /**
   * Aktualisiert ein Objekt
   * @param id
   * @param object
   * @param queueId Id der Queue für Idempotency
   */
  private update(id: number, object: any, queueId: string | null): Observable<T> {
    return this.http
      .put<T>(
        this.endpointWithUrl + id,
        this.formatPayload(object),
        this.httpOptions({ 'Idempotency-Key': object.client_id ?? queueId }),
      )
      .pipe(catchError(this.errorService.convert));
  }

  /**
   * Prüft ob es ein Offline Sync Problem gibt.
   * @param id
   */
  hasError(id: number): Promise<SyncError> {
    return Promise.resolve(new SyncError(200, {}));
  }

  /**
   * Löscht ein Objekt im Backend
   * @param id
   */
  delete(id: number): Observable<T> {
    return this.http
      .delete<T>(this.endpointWithUrl + id)
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  formatPayload(obj: T): string {
    const hashToSend: Record<string, unknown> = {};
    hashToSend[this.objectName] = this.cloneObjekt(obj);
    this.removeNotToUseParams(hashToSend);
    this.renameAttributedParams(hashToSend);
    return JSON.stringify(hashToSend);
  }
}
